import { useState, useCallback } from "react";
import { Spinner } from "@chakra-ui/react";
import { TextBoxHTML } from "newui/components/TextBoxHTML/TextBoxHTML";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { TextAreaWithLabel } from "newui/components/ui/InputWithLabel/TextAreaWithLabel";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { CheckboxWithLabel } from "newui/components/ui/CheckboxWithLabel";
import { ToggleButtonGroup } from "newui/components/ui/ToggleButtonGroup";
import { CompanyListing } from "../CompanyListing";
import { useDisclaimerModalData } from "../../hooks/useDisclaimerModalData";
import { useSubmitDisclaimer } from "../../hooks/useSubmitDisclaimer";
import { useCancelDisclaimer } from "../../hooks/useCancelDisclaimer";
import type { DisclaimerCompany } from "../../types/panel.type";
import Tooltip from "components/Tooltip";

interface DisclaimerModalProps {
	evalId: number;
	isOpen: boolean;
	isPreview?: boolean;
	memberId: number;
	previewData?: { companies: DisclaimerCompany[]; disclaimer_text: string; };
	rfxId: number;
	onClose: () => void;
	onSubmitSuccess?: () => void;
}

const NO_CONFLICT = "no_conflict";
const CONFLICT = "conflict";

const TOGGLE_OPTIONS = [
	{ label: "No Conflict", value: NO_CONFLICT },
	{ label: "Declare Conflict", value: CONFLICT },
];

export const DisclaimerModal: React.FC<DisclaimerModalProps> = ({
	isOpen,
	onClose,
	onSubmitSuccess,
	rfxId,
	evalId,
	memberId,
	isPreview = false,
	previewData,
}) => {
	const [conflictChoice, setConflictChoice] = useState<string | null>(null);
	const [conflictDetails, setConflictDetails] = useState("");
	const [conflictDetailsTouched, setConflictDetailsTouched] = useState(false);
	const [confirmed, setConfirmed] = useState(false);

	const { data, isLoading } = useDisclaimerModalData(
		rfxId,
		evalId,
		memberId,
		isOpen && !isPreview
	);
	const submitMutation = useSubmitDisclaimer(rfxId, evalId);
	const cancelMutation = useCancelDisclaimer(rfxId, evalId);

	const disclaimerText = isPreview
		? previewData?.disclaimer_text ?? ""
		: data?.disclaimer_text ?? "";
	const companies = isPreview
		? previewData?.companies ?? []
		: data?.companies ?? [];

	const hasConflict = conflictChoice === CONFLICT;
	const isSubmitDisabled =
		!conflictChoice ||
		!confirmed ||
		(hasConflict && conflictDetails.trim().length === 0);

	const handleSubmit = useCallback(() => {
		if (isPreview) {
			return;
		}
		submitMutation.mutate(
			{
				memberId,
				payload: {
					has_conflict: hasConflict,
					...(hasConflict
						? { conflict_details: conflictDetails.trim() }
						: {}),
					confirmed: true,
				},
			},
			{
				onSuccess: () => {
					if (onSubmitSuccess) {
						onSubmitSuccess();
					} else {
						onClose();
					}
				},
			}
		);
	}, [isPreview, submitMutation, memberId, hasConflict, conflictDetails, onClose, onSubmitSuccess]);

	const handleCancel = useCallback(() => {
		if (isPreview) {
			onClose();
			return;
		}
		cancelMutation.mutate(memberId, {
			onSuccess: () => {
				onClose();
			},
		});
	}, [isPreview, cancelMutation, memberId, onClose]);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Panel Member Disclaimer"
			iconType="info"
			size="6xl"
			customFooter={
				isPreview ? (
					<div className="flex justify-end">
						<Button
							btntype="secondary"
							onClick={onClose}
							data-testid="disclaimer-close-btn"
						>
							Close
						</Button>
					</div>
				) : (
					<div className="flex justify-end gap-2">
						<Button
							btntype="secondary"
							onClick={handleCancel}
							isLoading={cancelMutation.isPending}
							data-testid="disclaimer-cancel-btn"
						>
							Cancel
						</Button>
						<Tooltip label={isSubmitDisabled && "You must confirm that you have read the declaration and disclose any conflicts of interest before submitting."} data-testid="disclaimer-submit-tooltip">
							<Button
								btntype="primary"
								onClick={handleSubmit}
								disabled={isSubmitDisabled}
								isLoading={submitMutation.isPending}
								data-testid="disclaimer-submit-btn"
							>
								Submit
							</Button>
						</Tooltip>
					</div>
				)
			}
		>
			{isLoading ? (
				<div className="flex justify-center py-8">
					<Spinner data-testid="disclaimer-loading" />
				</div>
			) : (
				<div className="space-y-4" data-testid="disclaimer-modal-content">
					{isPreview && (
						<p
							className="text-xs font-medium uppercase text-gray-400"
							data-testid="disclaimer-preview-label"
						>
							Preview
						</p>
					)}

					<InstructionsInfoBox inline data-testid="disclaimer-info">
						Please read the declaration below in relation to the
						companies who have submitted tenders. You must declare
						any conflict of interest before proceeding.
					</InstructionsInfoBox>

					{/* Two-column: declaration on left, companies + controls on right */}
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						{/* Left column: scrollable declaration, max 2/3 viewport height */}
						<div>
							<p className="mb-1 text-xs font-medium text-gray-500">
								Declaration
							</p>
							<div
								className="max-h-[50vh] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4"
								data-testid="disclaimer-text"
							>
								<TextBoxHTML
									html={disclaimerText}
									className="text-sm text-gray-700 [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:mb-2 [&>h3]:text-base [&>h3]:font-semibold [&>h3]:mt-3 [&>h3]:mb-1"
								/>
							</div>
						</div>

						{/* Right column: companies + controls */}
						<div className="space-y-4">
							<div>
								<p className="mb-1 text-xs font-medium text-gray-500">
									Companies being evaluated
								</p>
								<CompanyListing companies={companies} />
							</div>

							<div>
								<p className="mb-2 text-sm font-medium text-gray-700">
									Do you have a conflict of interest?
								</p>
								<ToggleButtonGroup
									options={TOGGLE_OPTIONS}
									value={conflictChoice}
									onChange={(val: string) => {
										setConflictChoice(val);
										if (val !== CONFLICT) {
											setConflictDetailsTouched(false);
											setConflictDetails("");
										}
									}}
								/>
							</div>

							{hasConflict && (
								<TextAreaWithLabel
									label="Conflict details"
									id="disclaimer-conflict-details"
									required
									value={conflictDetails}
									onChange={(e) => {
										setConflictDetails(e.target.value);
										setConflictDetailsTouched(true);
									}}
									onBlur={() => setConflictDetailsTouched(true)}
									hasError={conflictDetailsTouched && conflictDetails.trim().length === 0}
									errorText={conflictDetailsTouched && conflictDetails.trim().length === 0 ? "Conflict details are required" : undefined}
									rows={4}
									noMargin
									showCharacterCount={false}
									testId="disclaimer-conflict-details-textarea"
								/>
							)}

							<div data-testid="disclaimer-confirm-checkbox">
								<CheckboxWithLabel
									checked={confirmed}
									onChange={(e) =>
										setConfirmed(e.target.checked)
									}
									label="I confirm that I have read and agree to the Confidentiality and Conflict of Interest Declaration and that I have disclosed any known conflicts of interest"
								/>
							</div>
						</div>
					</div>
				</div>
			)}
		</Modal>
	);
};
